import React, { useEffect, useState } from 'react';
import { FaPlus, FaRegWindowClose } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { depthAgainstPlan, hasCoreTrakRuns } from '../../drillholeSummary';

// The labelled lines under a drillhole's name. Each says what its number is -
// a bare count in a pill stopped being readable once there was more than one
// kind of count - and each is left out when there is nothing to say. The survey
// count comes from the map, so it shows even before the hole's summary arrives;
// depth and core runs need the summary.
export const DrillholeDetails = ({ drillhole, summary }) => {
    const { t } = useTranslation();
    const surveys = drillhole.surveys?.length || 0;
    const depth = depthAgainstPlan(summary, drillhole.metadata);
    const lines = [];
    if (depth != null) {
        lines.push(<span key="depth" title={t('drillholes.depthTitle')}>{t('drillholes.depthLine', { depth })}</span>);
    }
    if (surveys > 0) {
        lines.push(<span key="surveys">{t('drillholes.surveysLine', { count: surveys })}</span>);
    }
    if (hasCoreTrakRuns(summary)) {
        lines.push(<span key="core">{t('drillholes.coreRunsLine', { count: summary.coreTrakRuns })}</span>);
    }
    if (lines.length === 0) return null;
    // Right-aligned, so the names on the left read as one column when scanning
    // down a long list and the details keep out of their way.
    return (
        <small className="text-muted d-flex flex-column align-items-end text-end">
            {lines}
        </small>
    );
};

export const DrillholeList = ({ projectName, drillholes, summaries, handleDrillholeSelect, handleMapUpdated }) => {
    const { t } = useTranslation();
    const [hideNewDrillholeDetails, setHideNewDrillholeDetails] = useState(true);
    const [newDrillholeName, setNewDrillholeName] = useState('');
    const [newDrillholeRig, setNewDrillholeRig] = useState('');
    const [newDrillholeCollarX, setNewDrillholeCollarX] = useState('');
    const [newDrillholeCollarY, setNewDrillholeCollarY] = useState('');
    const [newDrillholeCollarZ, setNewDrillholeCollarZ] = useState('');
    const [newDrillholeDepth, setNewDrillholeDepth] = useState('');
    const [newDrillholeDip, setNewDrillholeDip] = useState('');
    const [newDrillholeAzimuth, setNewDrillholeAzimuth] = useState('');
    const [newDrillholeDescription, setNewDrillholeDescription] = useState('');
    const [drillholeFilter, setDrillholeFilter] = useState('');
    const [drillholeFilteredList, setDrillholeFilteredList] = useState(drillholes);

    const toggleNewDrillholeDetails = () => {
        setHideNewDrillholeDetails(!hideNewDrillholeDetails);
    }

    const handleNewDrillholeNameChange = (event) => {
        setNewDrillholeName(event.target.value);
    }

    const handleNewDrillholeRigChange = (event) => {
        setNewDrillholeRig(event.target.value);
    }

    const handleNewDrillholeCollarXChange = (event) => {
        setNewDrillholeCollarX(event.target.value);
    }

    const handleNewDrillholeCollarYChange = (event) => {
        setNewDrillholeCollarY(event.target.value);
    }

    const handleNewDrillholeCollarZChange = (event) => {
        setNewDrillholeCollarZ(event.target.value);
    }

    const handleNewDrillholeDepthChange = (event) => {
        setNewDrillholeDepth(event.target.value);
    }

    const handleNewDrillholeDipChange = (event) => {
        setNewDrillholeDip(event.target.value);
    }

    const handleNewDrillholeAzimuthChange = (event) => {
        setNewDrillholeAzimuth(event.target.value);
    }

    const handleNewDrillholeDescriptionChange = (event) => {
        setNewDrillholeDescription(event.target.value);
    }

    const handleNewDrillholeSubmit = (event) => {
        event.preventDefault();
        let x = parseFloat(newDrillholeCollarX);
        let y = parseFloat(newDrillholeCollarY);
        let z = parseFloat(newDrillholeCollarZ);
        if (newDrillholeName === '') {
            return;
        }
        if (isNaN(x)) {
            x = 0;
        }
        if (isNaN(y)) {
            y = 0;
        }
        if (isNaN(z)) {
            z = 0;
        }
        const newDrillhole = {
            name: newDrillholeName,
            rigId: newDrillholeRig,
            location: {
                x: x,
                y: y,
                z: z
            },
            description: newDrillholeDescription
        };
        console.log(JSON.stringify(newDrillhole, null, 2));
        fetch('api/projects/newdrillhole', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Project': projectName
            },
            body: JSON.stringify(newDrillhole)
        })
            .then(response => response.json())
            .then(data => {
                setNewDrillholeName('');
                setNewDrillholeRig('');
                setNewDrillholeCollarX('');
                setNewDrillholeCollarY('');
                setNewDrillholeCollarZ('');
                setNewDrillholeDescription('');
                setHideNewDrillholeDetails(true);
                handleMapUpdated(data);
                //handleDrillholeSelect(data.name);
            });
    }



    const handleDrillholeFilterChange = (event) => {
        setDrillholeFilter(event.target.value);
        const filteredList = drillholes.filter((drillhole) => {
            return drillhole.name.toLowerCase().includes(event.target.value.toLowerCase());
        });
        setDrillholeFilteredList(filteredList);
    }

    useEffect(() => {
        setDrillholeFilter('');
        setDrillholeFilteredList(drillholes);
    }, [drillholes])

    let newDrillholeDetails = '';

    if (!hideNewDrillholeDetails) {
        newDrillholeDetails = (
            <div className="card">
                <div className="card-header">
                    <h5 className="card-title">{t('drillholes.newDrillhole')}</h5>
                </div>
                <div className="card-body">
                    <form onSubmit={handleNewDrillholeSubmit}>
                        <div className="form-group">
                            <label htmlFor="newDrillholeName">{t('drillholes.nameLabel')}</label>
                            <input type="text" className="form-control" id="newDrillholeName" name="newDrillholeName" value={newDrillholeName} onChange={handleNewDrillholeNameChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeRig">{t('drillholes.rigLabel')}</label>
                            <input type="text" className="form-control" id="newDrillholeRig" name="newDrillholeRig" value={newDrillholeRig} onChange={handleNewDrillholeRigChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeCollarX">{t('drillholes.latitudeLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeCollarX" name="newDrillholeCollarX" value={newDrillholeCollarX} placeholder="0.0" onChange={handleNewDrillholeCollarXChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeCollarY">{t('drillholes.longitudeLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeCollarY" name="newDrillholeCollarY" value={newDrillholeCollarY} placeholder="0.0" onChange={handleNewDrillholeCollarYChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeCollarZ">{t('drillholes.elevationLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeCollarZ" name="newDrillholeCollarZ" value={newDrillholeCollarZ} placeholder="0.0" onChange={handleNewDrillholeCollarZChange} />
                        </div>
                        {/*<h5>Collar Coordinates</h5>*/}
                        {/*<div className="form-group">*/}
                        {/*    <label htmlFor="newDrillholeCollarX">X:</label>*/}
                        {/*    <input type="number" className="form-control" id="newDrillholeCollarX" name="newDrillholeCollarX" value={newDrillholeCollarX} placeholder="0.0" onChange={handleNewDrillholeCollarXChange} />*/}
                        {/*</div>*/}
                        {/*<div className="form-group">*/}
                        {/*    <label htmlFor="newDrillholeCollarY">Y:</label>*/}
                        {/*    <input type="number" className="form-control" id="newDrillholeCollarY" name="newDrillholeCollarY" value={newDrillholeCollarY} placeholder="0.0" onChange={handleNewDrillholeCollarYChange} />*/}
                        {/*</div>*/}
                        {/*<div className="form-group">*/}
                        {/*    <label htmlFor="newDrillholeCollarZ">Z:</label>*/}
                        {/*    <input type="number" className="form-control" id="newDrillholeCollarZ" name="newDrillholeCollarZ" value={newDrillholeCollarZ} placeholder="0.0" onChange={handleNewDrillholeCollarZChange} />*/}
                        {/*</div>*/}
                        <h5>{t('drillholes.plannedDirection')}</h5>
                        <div className="form-group">
                            <label htmlFor="newDrillholeDepth">{t('drillholes.depthLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeDepth" name="newDrillholeDepth" value={newDrillholeDepth} placeholder="0.0" onChange={handleNewDrillholeDepthChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeDip">{t('drillholes.dipLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeDip" name="newDrillholeDip" value={newDrillholeDip} placeholder="0.0" onChange={handleNewDrillholeDipChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeAzimuth">{t('drillholes.azimuthLabel')}</label>
                            <input type="number" className="form-control" id="newDrillholeAzimuth" name="newDrillholeAzimuth" value={newDrillholeAzimuth} placeholder="0.0" onChange={handleNewDrillholeAzimuthChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="newDrillholeDescription">{t('drillholes.descriptionLabel')}</label>
                            <textarea className="form-control" id="newDrillholeDescription" name="newDrillholeDescription" value={newDrillholeDescription} onChange={handleNewDrillholeDescriptionChange} />
                        </div>
                        <button type="submit" className="btn btn-secondary">{t('common.create')}</button>
                    </form>
                </div>
            </div>
        );
    }

    let drillholeListContents = '';

    if (drillholeFilteredList) {
        drillholeListContents = (
            <div className="list-group overflow-auto" style={{ maxHeight: 400 }}>
                {drillholeFilteredList.map(drillhole =>
                    // Stacked lines rather than inline figures, so the row reads the same
                    // at any width - a phone, or the md breakpoint where this list is a
                    // third of the page - with nothing to wrap.
                    <button
                        type="button"
                        className="list-group-item list-group-item-action"
                        key={drillhole.name}
                        onClick={() => handleDrillholeSelect(drillhole.name)}
                    >
                        <div>{drillhole.name}</div>
                        <DrillholeDetails drillhole={drillhole} summary={summaries?.[drillhole.name]} />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex">
                <h3>{t('drillholes.title')}</h3>
                {/*<button className="btn btn-primary ms-auto" style={{ marginRight: 10 }}><FaMapMarkedAlt /> Map</button>*/}

                <button className="btn btn-primary ms-auto" onClick={toggleNewDrillholeDetails}>{hideNewDrillholeDetails ? <span><FaPlus /> {t('common.new')}</span> : <span><FaRegWindowClose /> {t('common.cancel')}</span>}</button>
            </div>
            <br />
            {newDrillholeDetails}
            <input type="text" className="form-control" id="drillholeFilter" name="drillholeFilter" value={drillholeFilter} onChange={handleDrillholeFilterChange} placeholder={t('common.search')} />
            <br />
            {drillholeListContents}
        </div>
    );
}
